import React from 'react';
import './Products.css';
import './ProductsActions.css';
import './MobileResponsive.css';

const resolveAddedBy = (purchase) => {
  if (purchase.created_by_user) {
    return `${purchase.created_by_user.first_name || ''} ${purchase.created_by_user.last_name || ''}`.trim() || purchase.created_by_user.username;
  }
  if (typeof purchase.created_by === 'object' && purchase.created_by) {
    return `${purchase.created_by.first_name || ''} ${purchase.created_by.last_name || ''}`.trim() || purchase.created_by.username;
  }
  return purchase.added_by_name || purchase.created_by_name || 'N/A';
};

const ViewPurchaseModal = ({ purchase, isLoading, onClose, onPrint }) => {
  if (!purchase) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container invoice-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Purchase Details ( Reference No: {purchase.ref_no} )</h2>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-content invoice-content">
          {/* Invoice Header */}
          <div className="invoice-header-section">
            <div className="invoice-info-item">
              <strong>Reference No:</strong> #{purchase.ref_no}
            </div>
            <div className="invoice-info-item">
              <strong>Date:</strong> {purchase.transaction_date}
            </div>
            <div className="invoice-info-item">
              <strong>Purchase Status:</strong> {purchase.status}
            </div>
            <div className="invoice-info-item">
              <strong>Payment Status:</strong> {purchase.payment_status}
            </div>
            <div className="invoice-info-item">
              <strong>Location:</strong> {purchase.location?.name || "Lefty'S Liquor"}
            </div>
          </div>

          {/* Supplier Info */}
          <div className="customer-service-section">
            <div className="customer-info-box">
              <div className="info-item">
                <strong>Supplier:</strong> {purchase.contact?.name || purchase.supplier?.name || 'N/A'}
              </div>
              <div className="info-item">
                <strong>Business:</strong> {purchase.contact?.supplier_business_name || "Lefty'S Liquor"}
              </div>
              <div className="info-item">
                <strong>Address:</strong>
                <div>
                  {purchase.contact?.landmark || '14199 E HIGHWAY 12 ROGERS'}, <br />
                  {purchase.contact?.city || 'Little Rock'}, {purchase.contact?.state || 'AR'}, {purchase.contact?.country || 'USA'}
                </div>
              </div>
              <div className="info-item">
                <strong>Mobile:</strong> {purchase.contact?.mobile || 'N/A'}
              </div>
            </div>
            <div className="service-info-box">
              <div className="info-item">
                <strong>Added By:</strong> {resolveAddedBy(purchase)}
              </div>
            </div>
          </div>

          {/* Products Section */}
          <div className="invoice-section">
            <h3>Items Details:</h3>
            <div className="invoice-table-wrapper">
              <table className="invoice-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product Name</th>
                    <th>SKU</th>
                    <th>Purchase Quantity</th>
                    <th>Unit Cost (Before Disc)</th>
                    <th>Discount Percent</th>
                    <th>Unit Cost (Before Tax)</th>
                    <th>Subtotal (Before Tax)</th>
                    <th>Tax</th>
                    <th>Unit Cost Price (After Tax)</th>
                    <th>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="11" style={{ textAlign: 'center', padding: '3rem', color: 'var(--etpos-text-muted)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', flexDirection: 'column' }}>
                          <div className="spinner-loader" style={{ width: '1.75rem', height: '1.75rem', border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%' }}></div>
                          <span style={{ fontWeight: 500 }}>Loading item details...</span>
                        </div>
                      </td>
                    </tr>
                  ) : purchase.purchase_lines && purchase.purchase_lines.length > 0 ? (
                    purchase.purchase_lines.map((line, idx) => {
                      const qty = parseFloat(line.quantity || 0);
                      const priceBeforeDisc = parseFloat(line.pp_without_discount || line.purchase_price || 0);
                      const discPercent = parseFloat(line.discount_percent || 0);
                      const priceBeforeTax = parseFloat(line.purchase_price || 0);
                      const taxRate = parseFloat(line.product?.product_tax?.amount || line.tax_percent || 0);
                      const subtotalBeforeTax = priceBeforeTax * qty;
                      const taxAmt = subtotalBeforeTax * taxRate / 100;
                      const tax = parseFloat(line.item_tax || 0) || taxAmt;
                      const netCost = priceBeforeTax + (tax / (qty || 1));
                      const subtotal = priceBeforeTax * qty + tax;

                      return (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td style={{ fontWeight: 600 }}>{line.product?.name || `Product #${line.product_id}`}</td>
                          <td>{line.product?.sku || line.variations?.sub_sku || '--'}</td>
                          <td>{qty.toFixed(2)} {line.product?.unit?.short_name || 'Single'}</td>
                          <td>$ {priceBeforeDisc.toFixed(2)}</td>
                          <td>{discPercent.toFixed(2)} %</td>
                          <td>$ {priceBeforeTax.toFixed(2)}</td>
                          <td>$ {subtotalBeforeTax.toFixed(2)}</td>
                          <td>$ {tax.toFixed(2)}</td>
                          <td>$ {netCost.toFixed(2)}</td>
                          <td style={{ fontWeight: 700, color: '#2563eb' }}>$ {subtotal.toFixed(2)}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr><td colSpan="11" style={{ textAlign: 'center', padding: '3rem', color: 'var(--etpos-text-muted)' }}>No product items found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Payment and Totals Wrapper */}
          <div className="payment-totals-wrapper">
            <div className="invoice-section payment-info-section">
              <h3>Payment Info:</h3>
              <div className="invoice-table-wrapper">
                <table className="invoice-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Reference No</th>
                      <th>Amount</th>
                      <th>Payment Mode</th>
                      <th>Payment Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--etpos-text-muted)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', flexDirection: 'column' }}>
                            <div className="spinner-loader" style={{ width: '1.75rem', height: '1.75rem', border: '3px solid #e2e8f0', borderTopColor: '#10b981', borderRadius: '50%' }}></div>
                            <span style={{ fontWeight: 500 }}>Loading payments...</span>
                          </div>
                        </td>
                      </tr>
                    ) : purchase.payment_lines && purchase.payment_lines.length > 0 ? (
                      purchase.payment_lines.map((pmt, pIdx) => (
                        <tr key={pIdx}>
                          <td>{pIdx + 1}</td>
                          <td>{
                            (() => {
                              const d = pmt.paid_on;
                              if (!d || d.startsWith('0000') || d === '--') return '--';
                              const parsed = new Date(d);
                              return isNaN(parsed.getTime()) ? d : parsed.toLocaleString();
                            })()
                          }</td>
                          <td>{pmt.payment_ref_no || '--'}</td>
                          <td style={{ fontWeight: 700, color: '#059669' }}>$ {parseFloat(pmt.amount || 0).toFixed(2)}</td>
                          <td>{pmt.method}</td>
                          <td>{pmt.note || '--'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: 'var(--etpos-text-muted)' }}>No payments found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Activities Section */}
              <div style={{ marginTop: '2rem' }} className="invoice-section">
                <h3>Activities:</h3>
                <div className="invoice-table-wrapper">
                  <table className="invoice-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Action</th>
                        <th>By</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{purchase.transaction_date}</td>
                        <td>Added</td>
                        <td>{resolveAddedBy(purchase)}</td>
                        <td>Original purchase entry created.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Totals Section */}
            {(() => {
              const finalTotal    = parseFloat(purchase.final_total || 0);
              const netBeforeTax  = parseFloat(purchase.total_before_tax || finalTotal);
              const discountAmt   = parseFloat(purchase.discount_amount || 0);
              const shippingAmt   = parseFloat(purchase.shipping_charges || 0);
              const sumLineTax    = (purchase.purchase_lines || []).reduce((s, l) => {
                const qty  = parseFloat(l.quantity || 0);
                const rate = parseFloat(l.product?.product_tax?.amount || l.tax_percent || 0);
                const stored = parseFloat(l.item_tax || 0);
                return s + (stored > 0 ? stored : qty * parseFloat(l.purchase_price || 0) * rate / 100);
              }, 0);
              const storedPurchaseTax = parseFloat(purchase.tax_amount || 0);
              const derivedPurchaseTax = Math.max(0, finalTotal - (netBeforeTax - discountAmt) - sumLineTax - shippingAmt);
              const purchaseTaxDisplay = storedPurchaseTax > 0 ? storedPurchaseTax : derivedPurchaseTax;
              return (
                <div className="totals-section">
                  <div className="totals-grid">
                    <div className="total-row">
                      <span className="total-label">Net Total Amount:</span>
                      <span className="total-value">$ {netBeforeTax.toFixed(2)}</span>
                    </div>
                    <div className="total-row">
                      <span className="total-label">Discount:</span>
                      <span className="total-value">(-) $ {discountAmt.toFixed(2)}</span>
                    </div>
                    {sumLineTax > 0 && (
                      <div className="total-row">
                        <span className="total-label">Product Tax:</span>
                        <span className="total-value">(+) $ {sumLineTax.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="total-row">
                      <span className="total-label">Purchase Tax:</span>
                      <span className="total-value">(+) $ {purchaseTaxDisplay.toFixed(2)}</span>
                    </div>
                    <div className="total-row">
                      <span className="total-label">Additional Shipping charges:</span>
                      <span className="total-value">(+) $ {parseFloat(purchase.shipping_charges || 0).toFixed(2)}</span>
                    </div>
                    <div className="total-row total-payable-row">
                      <span className="total-label">Purchase Total:</span>
                      <span className="total-value">$ {parseFloat(purchase.final_total || 0).toFixed(2)}</span>
                    </div>
                    <div className="total-row">
                      <span className="total-label">Amount Paid:</span>
                      <span className="total-value" style={{ color: '#10b981' }}>$ { (parseFloat(purchase.final_total || 0) - parseFloat(purchase.payment_due || 0)).toFixed(2) }</span>
                    </div>
                    <div className="total-row">
                      <span className="total-label">Payment Due:</span>
                      <span className="total-value" style={{ color: '#ef4444' }}>$ {parseFloat(purchase.payment_due || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Shipping Details */}
          <div className="invoice-section">
            <h3>Shipping Details:</h3>
            <div className="shipping-info-box" style={{ background: 'rgba(255,255,255,0.5)', border: '1px solid var(--etpos-border-light)', padding: '1.25rem', borderRadius: '8px' }}>
              <div style={{ color: 'var(--etpos-text)', fontWeight: 500, lineHeight: '1.4' }}>
                {purchase.shipping_details || '--'}
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <div className="notes-section">
            <div className="note-item">
              <h4>Additional Notes:</h4>
              <p>{purchase.additional_notes || '--'}</p>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="modal-btn print-invoice-btn" onClick={() => onPrint && onPrint(purchase)}>
            🖨️ Print Purchase
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewPurchaseModal;
